// Tier-2 <Select> = label + field + error, backed by AntD Select (FORT-182).
// Options are passed as data (label/value) rather than <Option> children, and
// the value/onChange contract drops into an AntD <Form.Item>. Label/error
// spacing and colour come from tokens.
//
// Options may be grouped (a `{ label, options }` entry renders an OptGroup), and
// `optionLabelProp` lets the closed control show a terser label than the list
// row — needed when a row carries rich content (a status pill, say) that would
// be noise once collapsed.
import React from 'react';
import { Select as AntSelect } from 'antd';
import { useTokens } from '../../core/theme/TokensProvider';
import type { CommonProps } from './types';

export type SelectValue = string | number;

export interface SelectOption {
  label: React.ReactNode;
  value: SelectValue;
  disabled?: boolean;
  // Plain-text label used by the closed control when optionLabelProp="title"
  // (and as the option's native tooltip).
  title?: string;
}

export interface SelectOptionGroup {
  label: React.ReactNode;
  options: SelectOption[];
}

export interface SelectProps extends CommonProps {
  label?: string;
  error?: string;
  value?: SelectValue | SelectValue[];
  defaultValue?: SelectValue | SelectValue[];
  // `undefined` is emitted when `allowClear` clears the selection.
  onChange?: (value: SelectValue | SelectValue[] | undefined) => void;
  options: (SelectOption | SelectOptionGroup)[];
  // Which field of an option the *closed* control renders. Defaults to 'label'.
  optionLabelProp?: 'label' | 'title';
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  showSearch?: boolean;
  mode?: 'multiple' | 'tags';
  id?: string;
  // For a select with no visible `label` — the field still needs a name.
  'aria-label'?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  value,
  defaultValue,
  onChange,
  options,
  optionLabelProp,
  placeholder,
  disabled,
  allowClear,
  showSearch,
  mode,
  id,
  className,
  style,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}) => {
  const tokens = useTokens();
  const generatedId = React.useId();
  const selectId = id ?? generatedId;
  // For field primitives, className/style hook the label+field wrapper, while
  // data-testid lands on the field itself — that's the node a test types into or
  // selects, so it's the useful target here.
  return (
    <div
      className={className}
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[1], ...style }}
    >
      {label && (
        <label
          htmlFor={selectId}
          style={{ fontSize: tokens.fontSize.sm, fontWeight: tokens.fontWeight.medium }}
        >
          {label}
        </label>
      )}
      <AntSelect
        id={selectId}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        options={options}
        optionLabelProp={optionLabelProp}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={allowClear}
        showSearch={showSearch}
        // AntD's default filter matches against `label`, which filters nothing
        // when the label is a rich node; fall back to the option's plain-text
        // title in that case.
        filterOption={
            showSearch
                ? (input, option) => {
                      const text =
                          typeof option?.label === 'string'
                              ? option.label
                              : ((option as SelectOption | undefined)?.title ?? '');
                      return text.toLowerCase().includes(input.toLowerCase());
                  }
                : undefined
        }
        mode={mode}
        status={error ? 'error' : undefined}
        style={{ width: '100%' }}
        aria-label={ariaLabel}
        data-testid={dataTestId}
      />
      {error && (
        <span style={{ fontSize: tokens.fontSize.sm, color: tokens.color.danger }}>{error}</span>
      )}
    </div>
  );
};
